import { createReducer, on } from '@ngrx/store';
import * as AssetManagementActions from './asset-management.actions';
import { PaginationResponse } from '@shared/models/common.model';

import {
  AssetNetwork,
  BalanceHistoryData,
  Network,
  WhitelistAddress,
  WhitelistAddressMinimal,
  WhitelistAddressSort,
} from '../models/asset-management.model';

export interface AssetManagementState {
  networks: Network[];
  networksLoading: boolean;
  networksError: string | null;
  assetNetworks: AssetNetwork[];
  assetNetworksLoading: boolean;
  assetNetworksError: string | null;
  whitelistAddresses: WhitelistAddress[];
  whitelistAddressesLoading: boolean;
  whitelistAddressesError: string | null;
  whitelistAddressesPagination: PaginationResponse;
  whitelistAddressesSort: WhitelistAddressSort;
  whitelistAddressesSearchTerm: string;
  whitelistAddressesMinimal: WhitelistAddressMinimal[];
  whitelistAddressesMinimalLoading: boolean;
  whitelistAddressesMinimalError: string | null;
  balanceHistory: BalanceHistoryData | null;
  balanceHistoryLoading: boolean;
  balanceHistoryError: string | null;
}

export const initialState: AssetManagementState = {
  networks: [],
  networksLoading: false,
  networksError: null,
  assetNetworks: [],
  assetNetworksLoading: false,
  assetNetworksError: null,
  whitelistAddressesLoading: false,
  whitelistAddressesError: null,
  whitelistAddresses: [],

  whitelistAddressesPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  whitelistAddressesSort: {
    sortBy: 'createdAt',
    sortDirection: 'DESC',
  },
  whitelistAddressesSearchTerm: '',
  whitelistAddressesMinimal: [],
  whitelistAddressesMinimalLoading: false,
  whitelistAddressesMinimalError: null,
  balanceHistory: null,
  balanceHistoryLoading: false,
  balanceHistoryError: null,
};

export const assetManagementReducer = createReducer(
  initialState,

  // Load Networks
  on(AssetManagementActions.loadNetworks, state => ({
    ...state,
    networksLoading: true,
    networksError: null,
    networks: [],
  })),

  on(AssetManagementActions.loadNetworksSuccess, (state, { response }) => ({
    ...state,
    networksLoading: false,
    networksError: null,
    networks: response.data || [],
  })),

  on(AssetManagementActions.loadNetworksFailure, (state, { error }) => ({
    ...state,
    networksLoading: false,
    networksError: error,
    networks: [],
  })),

  // Load Asset Networks
  on(AssetManagementActions.loadAssetNetworks, state => ({
    ...state,
    assetNetworksLoading: true,
    assetNetworksError: null,
    assetNetworks: [],
  })),

  on(
    AssetManagementActions.loadAssetNetworksSuccess,
    (state, { assetNetworks }) => ({
      ...state,
      assetNetworksLoading: false,
      assetNetworksError: null,
      assetNetworks,
    })
  ),

  on(AssetManagementActions.loadAssetNetworksFailure, (state, { error }) => ({
    ...state,
    assetNetworksLoading: false,
    assetNetworksError: error,
    assetNetworks: [],
  })),

  // Load Whitelist Addresses
  on(AssetManagementActions.loadWhitelistAddresses, state => ({
    ...state,
    whitelistAddressesLoading: true,
    whitelistAddressesError: null,
    whitelistAddresses: [],
  })),

  on(
    AssetManagementActions.loadWhitelistAddressesSuccess,
    (state, { data, pagination }) => ({
      ...state,
      whitelistAddressesLoading: false,
      whitelistAddressesError: null,
      whitelistAddresses: data || [],
      whitelistAddressesPagination: pagination,
    })
  ),

  on(
    AssetManagementActions.loadWhitelistAddressesFailure,
    (state, { error }) => ({
      ...state,
      whitelistAddressesLoading: false,
      whitelistAddressesError: error,
      whitelistAddresses: [],
    })
  ),

  // Load Whitelist Addresses (minimal, network-scoped)
  on(AssetManagementActions.loadWhitelistAddressesMinimal, state => ({
    ...state,
    whitelistAddressesMinimalLoading: true,
    whitelistAddressesMinimalError: null,
    whitelistAddressesMinimal: [],
  })),

  on(
    AssetManagementActions.loadWhitelistAddressesMinimalSuccess,
    (state, { data }) => ({
      ...state,
      whitelistAddressesMinimalLoading: false,
      whitelistAddressesMinimalError: null,
      whitelistAddressesMinimal: data || [],
    })
  ),

  on(
    AssetManagementActions.loadWhitelistAddressesMinimalFailure,
    (state, { error }) => ({
      ...state,
      whitelistAddressesMinimalLoading: false,
      whitelistAddressesMinimalError: error,
      whitelistAddressesMinimal: [],
    })
  ),

  on(AssetManagementActions.clearWhitelistAddressesMinimal, state => ({
    ...state,
    whitelistAddressesMinimal: [],
    whitelistAddressesMinimalLoading: false,
    whitelistAddressesMinimalError: null,
  })),

  // Pagination
  on(AssetManagementActions.setPage, (state, { pageIndex }) => ({
    ...state,
    whitelistAddressesPagination: {
      ...state.whitelistAddressesPagination,
      page: pageIndex + 1,
    },
  })),

  on(AssetManagementActions.setPageSize, (state, { pageSize }) => ({
    ...state,
    whitelistAddressesPagination: {
      ...state.whitelistAddressesPagination,
      limit: pageSize,
      page: 1,
    },
  })),

  // Sort
  on(AssetManagementActions.setSort, (state, { sort }) => ({
    ...state,
    whitelistAddressesSort: sort,
    whitelistAddressesPagination: {
      ...state.whitelistAddressesPagination,
      page: 1,
    },
  })),

  // Filters
  on(AssetManagementActions.clearFilters, state => ({
    ...state,
    whitelistAddressesSearchTerm: '',
    whitelistAddressesPagination: {
      ...state.whitelistAddressesPagination,
      page: 1,
    },
  })),

  // Search
  on(AssetManagementActions.setSearch, (state, { searchTerm }) => ({
    ...state,
    whitelistAddressesSearchTerm: searchTerm,
    whitelistAddressesPagination: {
      ...state.whitelistAddressesPagination,
      page: 1,
    },
  })), // Load Balance History
  on(AssetManagementActions.loadBalanceHistory, state => ({
    ...state,
    balanceHistoryLoading: true,
    balanceHistoryError: null,
  })),

  // Load Balance History Success
  on(AssetManagementActions.loadBalanceHistorySuccess, (state, { data }) => ({
    ...state,
    balanceHistory: data,
    balanceHistoryLoading: false,
    balanceHistoryError: null,
  })),

  // Load Balance History Failure
  on(AssetManagementActions.loadBalanceHistoryFailure, (state, { error }) => ({
    ...state,
    balanceHistoryLoading: false,
    balanceHistoryError: error,
  }))
);
